import { Button } from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';

import {
  type GalleryImage,
  PropertyImageGalleryModal,
} from './PropertyImageGalleryModal';
import { type Photo } from '../../types';

type Props = {
  photos: Photo[];
  propertyName: string;
  className?: string;
};

const MAX_IMAGES_COUNT = 4;

export const PropertyImageGallery = ({
  photos,
  propertyName,
  className,
}: Props) => {
  const { t } = useTranslation('global');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalInitialIndex, setModalInitialIndex] = useState(0);

  const galleryImages: GalleryImage[] = photos.map((photo, index) => ({
    src: photo.url,
    alt: `${propertyName} - Photo ${index + 1}`,
  }));

  const secondaryImages = galleryImages.slice(1, MAX_IMAGES_COUNT);
  const remainingImagesCount = Math.max(
    galleryImages.length - MAX_IMAGES_COUNT,
    0,
  );

  const openModalAt = (index: number) => {
    setModalInitialIndex(index);
    setIsModalOpen(true);
  };
  const firstImageIndex = 0;
  const firstImage = galleryImages[firstImageIndex];

  const closeModal = () => setIsModalOpen(false);

  if (galleryImages.length === 0) {
    return (
      <div
        className={classNames(
          'rounded-12 bg-background-secondary-default flex h-[400px] items-center justify-center',
          className,
        )}
      >
        <p className="body-m text-content-secondary-bg-primary">
          No images available
        </p>
      </div>
    );
  }

  return (
    <>
      <div
        className={classNames(
          'rounded-12 flex flex-col gap-8 overflow-hidden',
          className,
        )}
      >
        <div className="h-[400px] w-full overflow-hidden">
          <Image
            src={firstImage.src}
            alt={firstImage.alt}
            onClick={() => openModalAt(firstImageIndex)}
          />
        </div>
        {secondaryImages.length > 0 && (
          <div className="flex gap-8">
            {secondaryImages.map((image, index) => {
              const absoluteIndex = index + 1;
              const showOverlay =
                remainingImagesCount > 0 &&
                absoluteIndex === secondaryImages.length;

              return (
                <div
                  key={`${image.src}-${absoluteIndex}`}
                  className="relative h-[190px] flex-1 overflow-hidden"
                >
                  <Image
                    src={image.src}
                    alt={image.alt}
                    className={showOverlay ? 'brightness-30' : undefined}
                    onClick={() => openModalAt(absoluteIndex)}
                  />
                  {showOverlay && (
                    <>
                      <div className="bg-overlay absolute inset-0" />
                      <Button
                        variant="tertiaryComplementary"
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                        text={t(
                          'travel.propertyDetails.propertyImageGallery.viewMorePhotos',
                          { count: remainingImagesCount },
                        )}
                        iconName="photo"
                        iconPosition="left"
                        onClick={() => openModalAt(absoluteIndex)}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <PropertyImageGalleryModal
        images={galleryImages}
        title={propertyName}
        initialIndex={modalInitialIndex}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </>
  );
};
const Image = ({
  src,
  alt,
  className,
  onClick,
}: {
  src: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}) => {
  const imageElement = (
    <img
      src={src}
      alt={alt}
      className={classNames(
        'h-full w-full object-cover',
        onClick && 'cursor-pointer hover:brightness-92',
        className,
      )}
    />
  );
  if (!onClick) {
    return imageElement;
  }
  return (
    <button
      type="button"
      className="focus-ring h-full w-full overflow-hidden border-none bg-transparent p-0 text-left"
      onClick={onClick}
      aria-label={alt}
    >
      {imageElement}
    </button>
  );
};
